import type { Chain } from '@particle-network/chains';
import { UserInfo } from '../api/model/user-info';
import { load, PreferenceKey, removeItem, save } from '../repository';
import { clearLocalKeyAndLocalKMSDataKey } from './../api/master-password';
import { UserPreview } from './../api/model/user-info';
import { BasicQuery, CurrencyUnit, EvmSignMethod, LoginQuery, SignQuery, SolanaSignMethod, Theme } from './bundle';

class TokenProvider {
    private _queryParam: LoginQuery | SignQuery | BasicQuery | undefined;

    private _paymentToken: string | undefined;

    fiatCoin: CurrencyUnit = 'USD';

    erc4337 = false;

    get secretKey(): string {
        return sessionStorage.getItem(PreferenceKey.PN_TEMP_SECRET_KEY)!;
    }

    get token(): string {
        return this.userInfo.token;
    }

    get themeType(): string | null {
        const themeType = sessionStorage.getItem(PreferenceKey.PN_TEMP_THEME_TYPE);
        return themeType;
    }

    get queryParam(): LoginQuery | SignQuery | BasicQuery {
        if (!this._queryParam) {
            const params = sessionStorage.getItem(PreferenceKey.PN_TEMP_CLIENT_PARAMS);
            if (!params) {
                return {} as any;
            }
            this._queryParam = JSON.parse(params);

            // @ts-ignore
            this._queryParam && (this._queryParam.login_form_mode = false);

            const themeType = this.themeType;
            if (themeType && this._queryParam) {
                this._queryParam.theme_type = themeType as Theme;
            }

            let authorization = (this._queryParam as LoginQuery)?.authorization;
            const isEvm = this._queryParam?.chain_name?.toLowerCase?.() !== 'solana';
            if (authorization?.message && this._queryParam) {
                this._queryParam.message = authorization?.message;
                this._queryParam.method = isEvm
                    ? authorization?.uniq
                        ? EvmSignMethod.personal_sign_uniq
                        : EvmSignMethod.personal_sign
                    : SolanaSignMethod.signMessage;
            }
        }
        return this._queryParam!;
    }

    get chain(): Chain {
        let name = this._queryParam?.chain_name?.toLocaleLowerCase();
        if (this._queryParam && name) {
            // TODO: remove this when chain name is fixed
            if (name === 'mapo') {
                name = 'mapprotocol';
            } else if (name === 'conflux') {
                name = 'confluxespace';
            } else if (name === 'polygon zkevm') {
                name = 'polygonzkevm';
            }
            if ('chain_id' in this._queryParam) {
                const id = this._queryParam.chain_id;
                return {
                    name,
                    id,
                };
            } else {
                return {
                    name: name,
                    id: 0, // not care chain id
                };
            }
        } else {
            return {
                name: 'Ethereum',
                id: 1,
            };
        }
    }

    get userPreview(): UserPreview {
        const { mac_key, wallets, cognito_result, id, ...preview } = this.userInfo;
        return preview;
    }

    get userInfo(): UserInfo {
        const info = load(PreferenceKey.PN_USER_INFO);
        return JSON.parse(info ?? '{}');
    }

    set userInfo(userInfo: UserInfo) {
        save(PreferenceKey.PN_USER_INFO, JSON.stringify(userInfo));
        const params = this.queryParam;
        if (params.token !== userInfo.token) {
            params.token = userInfo.token;
            sessionStorage.setItem(PreferenceKey.PN_TEMP_CLIENT_PARAMS, JSON.stringify(params));
        }
    }

    get paymentToken(): string | undefined {
        return this._paymentToken;
    }

    set paymentToken(token: string | undefined) {
        this._paymentToken = token;
    }

    isLogin(): boolean {
        const info = load(PreferenceKey.PN_USER_INFO);
        if (info) {
            const userInfo = JSON.parse(info);
            return userInfo.token && userInfo.token === this.queryParam.token;
        }
        return false;
    }

    logout() {
        try {
            clearLocalKeyAndLocalKMSDataKey();
            removeItem(PreferenceKey.PN_USER_INFO);
        } catch (error) {
            //ignore
        }
    }
}

const tokenProvider: TokenProvider = new TokenProvider();
export default tokenProvider;
